import React, { useEffect, useState } from 'react';
import clipboardCross from 'assets/clipboardCross.svg';
import clsx from 'clsx';
import { CurrencyPair } from 'components/International';
import BackdropLoader from 'components/Molecules/Loaders/BackdropLoader/BackdropLoader';
import VOPStatusBadge from 'components/Payments/VOP/VOPStatusBadge';
import EmptyTable from 'components/Table/EmptyTable';
import VOPCheckTooltip from 'components/VOPCheckTooltip/VOPCheckTooltip';
import { ITableColumn } from 'services/DatabaseServices/marker.interface';
import { formatNumber } from 'utils/currency.helpers';
import formatIsoDate from 'utils/formatIsoDate';
import mapPaymentOrigin from 'utils/payments/mapPaymentOrigin';
import mapPaymentStatusDisplay from 'utils/payments/mapPaymentStatusDisplay';
import t from 'utils/translationHelper';

import { Approver } from '@alpha/auth-dtos';
import { PaymentBatchDto, PaymentDto, PaymentVopMatchStatus } from '@alpha/payments-dtos';
import { Box, Button, Tooltip } from '@alpha/ui-lib/ui/external';
import { Status } from '@alpha/ui-lib/ui/Status';
import { StyledGenericTable } from '@alpha/ui-lib/ui/table';
import { faExclamationCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import ActionDropDown from '../../../FxTradeTable/DisplayTable/ActionDropDown/ActionDropDown';
import NoOfApprovers from '../../../FxTradeTable/DisplayTable/NoOfApprovers/NoOfApprovers';
import PaymentActionBar from '../../PaymentActionBar/PaymentActionBar';

import useStyles from './BatchPaymentsTable.styles';

interface IProps {
  payments: PaymentDto[],
  paymentBatch: PaymentBatchDto | undefined,
  loading: boolean,
  selectedIds: string[],
  setSelectedIds: (ids: string[]) => void,
  handleBeneficiaryButton: (beneficiaryId: string) => void,
  setRejectedPaymentIds: (rejectedIds: string[]) => void,
  setOpenApproveModal: (open: boolean) => void,
  setOpenRejectModal: (open: boolean) => void,
  setPaymentsToApprove: (payments: PaymentDto[]) => void,
  setPaymentsToReject: (payments: PaymentDto[]) => void,
  handleReloadTable?: () => void,
  setSelectedViewPayment: React.Dispatch<React.SetStateAction<PaymentDto | undefined>>,
  handleApprovePayments: (payments: PaymentDto[]) => void,
  handleRejectPayments: (payments: PaymentDto[]) => void,
  setPaymentNeedsVOPConfirmation?: (payment: PaymentDto) => void,
}
/* eslint-disable max-lines-per-function */

const BatchPaymentsTable: React.FC<IProps> = ({
  payments,
  paymentBatch,
  loading,
  selectedIds,
  setSelectedIds,
  handleBeneficiaryButton,
  setRejectedPaymentIds,
  setOpenApproveModal,
  setOpenRejectModal,
  setPaymentsToApprove,
  setPaymentsToReject,
  handleReloadTable,
  setSelectedViewPayment,
  handleApprovePayments,
  handleRejectPayments,
  setPaymentNeedsVOPConfirmation,
} : IProps) => {

  const columns: ITableColumn[] = [
    { header: t('payment_id'), accessor: 'contractNumber' },
    {
      header: t('source'), accessor: 'source', sortable: true, width: 120,
    },
    { header: t('beneficiary'), accessor: 'beneficiaryName' },
    { header: t('value_date'), accessor: 'valueDate' },
    { header: t('reference'), accessor: 'reference' },
    { header: t('amount'), accessor: 'instructedAmount' },
    { header: t('details'), accessor: 'details' },
    { header: t('status'), accessor: 'paymentStatus', width: 200 },
    { header: t('actions'), accessor: 'actions', align: 'center' },
  ];


  const [multiSelectedPayments, setMultiSelectedPayments] = useState<PaymentDto[]>([]);
  const [displayMultiSelect, setDisplayMultiSelect] = useState(false);
  const [currentOptions, setCurrentOptions] = useState<Approver[]>([]);
  const [textValue, setTextValue] = useState('');

  const classes = useStyles();

  const handleClearButtonClick = () => {
    setSelectedIds([]);
  };

  const mapPaymentObjectWithSelectedIds = (selectedPaymentIds: string[]): void => {
    const resultPayments: PaymentDto[] = multiSelectedPayments?.filter(
      (payment) => selectedPaymentIds?.includes(payment.id),
    ) || [];

    const multiSelectedPaymentsIds = multiSelectedPayments?.map((payment) => payment.id);
    const newlySelectedIds = selectedPaymentIds?.filter(
      (paymentId) => !multiSelectedPaymentsIds?.includes(paymentId),
    );

    const paymentData: PaymentDto[] = payments || [];
    const newlySelected: PaymentDto[] = paymentData.filter(
      (item) => newlySelectedIds.includes(item.id),
    );

    resultPayments.push(...newlySelected);

    setMultiSelectedPayments(resultPayments);
    setPaymentsToApprove(resultPayments);
    setPaymentsToReject(resultPayments);
  };

  const formattedTableData = payments.map((payment: PaymentDto) => { 
    const vopAcknowledgeRequired = payment.vopRequired 
    && !payment.vopDetails?.vopApprovalAcknowledged;
    return {
    ...payment,
    debitingCurrency: (payment.debitingCurrency && (
      <CurrencyPair currencyCode={payment.debitingCurrency} />
    )) || '-',
    source: <span className={clsx(vopAcknowledgeRequired && classes.vopAcknowledgeRequiredCell)}>{mapPaymentOrigin(payment.origin)}</span>,
    contractNumber: (
      <Box
      display="flex"
      alignItems="left"
      justifyContent="flex-start"
      gridColumnGap="8px"
    >
      {vopAcknowledgeRequired && (
        <Tooltip title={t('vop_acknowledge_required')} classes={{ tooltip: classes.tooltip }} placement="bottom-start">
          <div style={{ marginTop: '2px', color: '#CF752F' }}>
            <FontAwesomeIcon icon={faExclamationCircle} />
          </div>
        </Tooltip>
      )}
      <Button
        disableRipple
        className={clsx(classes.tableButton, vopAcknowledgeRequired && classes.vopAcknowledgeRequiredCell, classes.underline)}
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          if (setSelectedViewPayment) { setSelectedViewPayment(payment); }
        }}
      >
        {payment.contractNumber || '-'}
      </Button>
    </Box>
    ),
    instructedAmount: <span className={classes.amount}>{[payment.debitingCurrency, formatNumber(payment.amount, 2)].join(' ')}</span>,
    valueDate: formatIsoDate(payment.valueDate),
    beneficiaryName: (
      <Button
        disableRipple
        className={clsx(classes.tableButton, vopAcknowledgeRequired && classes.vopAcknowledgeRequiredCell, classes.underline)}
        style={{ minWidth: '150px', margin: '0px 0px 0px 0px' }}
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          if (handleBeneficiaryButton) { handleBeneficiaryButton(payment.beneficiaryId); }
        }}
      >
        <Box display="flex" alignItems="left" justifyContent="flex-start">
          {payment.vopRequired && (
          <VOPCheckTooltip
            vopCheck={payment.vopDetails?.status ?? PaymentVopMatchStatus.NOT_AVAILABLE}
            vopAccepted={payment.vopDetails?.vopApprovalAcknowledged}
          >
            <VOPStatusBadge
              status={payment.vopDetails?.status}
              onClick={(e) => {
                e.stopPropagation();
                setPaymentNeedsVOPConfirmation?.(payment);
              }}
            />
          </VOPCheckTooltip>
          )}
          <span style={{ marginTop: '3px', marginLeft: '5px' }}>{payment.beneficiaryName || '-'}</span>
        </Box>
      </Button>),
    details: (payment
      && (
        <NoOfApprovers
          uploadedBy={payment.uploadedBy}
          uploadedDate={payment.uploadedDate}
          noOfApprovals={payment.approvalInfo.approvals?.length}
          approvedBy={payment.approvalInfo.approvals || []}
          approvalsRequired={payment.approvalInfo.approvalsRequired}
        />
      )
    ) || '-',
    paymentStatus: (
      <Status className={classes.status} variant={mapPaymentStatusDisplay(payment.status).variant}>{t(mapPaymentStatusDisplay(payment.status).text) || '-'}</Status>),
    paymentStatusStr: payment.status,
    paymentDto: payment,
    actions: (
      <ActionDropDown
        payment={payment}
        setDisplayMultiSelect={setDisplayMultiSelect}
        displayMultiSelect={displayMultiSelect}
        setSelectedPayment={setSelectedViewPayment}
        handleReloadPaymentTable={handleReloadTable}
        handleApprovePayments={handleApprovePayments}
        handleRejectPayments={handleRejectPayments}
        currentOptions={currentOptions}
        setCurrentOptions={setCurrentOptions}
        setTextValue={setTextValue}
    />
    )
  }});

  const showActionBar = multiSelectedPayments && multiSelectedPayments.length > 0;

  useEffect(() => {
    mapPaymentObjectWithSelectedIds(selectedIds);
  }, [selectedIds]);

  if (loading) return <BackdropLoader testId="batch-payments-loader" />;

  if (payments.length) {
    return (
      <div className={classes.tableWrapper}>
        <StyledGenericTable
          testId="batch-payments-table"
          columns={columns}
          data={formattedTableData}
          multiSelectable
          keyColumn="id"
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          totalRowNumber={formattedTableData.length}
        />
        <PaymentActionBar
          selectedPayments={multiSelectedPayments}
          handleClearButtonClick={handleClearButtonClick}
          handleOpenApprovalDrawer={() => setOpenApproveModal(true)}
          handleRejectModalOpen={() => setOpenRejectModal(true)}
          show={showActionBar}
        />
      </div>
    );
  }

  return (
    <EmptyTable
      title="No Batch Payments"
      subtitle="There are no payments associated with this batch."
      icon={clipboardCross}
    />
  );
};

export default BatchPaymentsTable;
